import Client from "fhir-kit-client";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
} from "react";
import { useNavigate } from "react-router";
import {
  Bundle,
  Device,
  FhirResource,
  Patient,
  Practitioner,
  RelatedPerson,
} from "fhir/r4";
import AcesoPage from "src/components/AcesoPage/AcesoPage";
import NoteForm from "src/components/NoteForm/NoteForm";

const CreateNote: FunctionComponent = () => {
  const navigate = useNavigate();

  const fhirUrl = process.env.REACT_APP_FHIR_URL;

  if (!fhirUrl) {
    throw new Error("REACT_APP_FHIR_URL n'est pas définie");
  }

  const client = new Client({
    baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
  });

  const onError = useCallback((error?: unknown) => {
    navigate("/Error", { state: { error } });
  }, [navigate]);

  const toNoteList = useCallback(() => {
    navigate("/NotesPage");
  }, [navigate]);

  function createNote(bundle: Bundle): void {
    client
      .transaction({
        body: bundle as FhirResource & { type: "transaction" },
      })
      .then((response) => {
        console.log(response);
        toNoteList();
      })
      .catch((error) => {
        console.error("Erreur lors de la création de la note :", error);
        onError();
      });
  }

  async function getPatients(
    setter: Dispatch<SetStateAction<Patient[]>>,
  ): Promise<void> {
    try {
      const response = (await client.search({
        resourceType: "Patient",
        searchParams: {
          _elements: "id,name",
        },
      })) as Bundle;

      const result: Patient[] = [];

      response.entry?.forEach((entry) => {
        if (entry.resource) {
          result.push(entry.resource as Patient);
        }
      });

      setter(result);
    } catch (error) {
      console.error("Erreur lors du chargement des patients :", error);
      setter([]);
    }
  }

  async function getPractitioners(
    setter: Dispatch<SetStateAction<Practitioner[]>>,
  ): Promise<void> {
    try {
      const response = (await client.search({
        resourceType: "Practitioner",
        searchParams: {
          _elements: "id,name",
        },
      })) as Bundle;

      const result: Practitioner[] = [];

      response.entry?.forEach((entry) => {
        if (entry.resource) {
          result.push(entry.resource as Practitioner);
        }
      });

      setter(result);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des praticiens :",
        error,
      );
      setter([]);
    }
  }

  async function getRelatedPerson(
    setter: Dispatch<SetStateAction<RelatedPerson[]>>,
  ): Promise<void> {
    try {
      const response = (await client.search({
        resourceType: "RelatedPerson",
        searchParams: {
          _elements: "id,name",
        },
      })) as Bundle;

      const result: RelatedPerson[] = [];

      response.entry?.forEach((entry) => {
        if (entry.resource) {
          result.push(entry.resource as RelatedPerson);
        }
      });

      setter(result);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des personnes liées :",
        error,
      );
      setter([]);
    }
  }

  async function getDevices(
    setter: Dispatch<SetStateAction<Device[]>>,
  ): Promise<void> {
    try {
      const response = (await client.search({
        resourceType: "Device",
        searchParams: {
          _elements: "id,deviceName",
        },
      })) as Bundle;

      const result: Device[] = [];

      response.entry?.forEach((entry) => {
        if (entry.resource) {
          result.push(entry.resource as Device);
        }
      });

      setter(result);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des appareils :",
        error,
      );
      setter([]);
    }
  }

  return (
    <AcesoPage
      loading={false}
      titleKey="title.createnote"
      needsLogin={true}
    >
      <NoteForm
        displayButtons={true}
        getPatientChoices={getPatients}
        getPractitionerChoices={getPractitioners}
        getRelatedPersonChoices={getRelatedPerson}
        getDeviceChoices={getDevices}
        noteSubmit={createNote}
      />
    </AcesoPage>
  );
};

export default CreateNote;
